// src/pages/Profil/Profil.tsx (updated)
import "assets/scss/style.scss";
import "./Profil.scss";

import Button, { Button_Style, Button_Type } from "shared/ui/Button/Button";
import { FC, useContext, useEffect, useState } from "react";

import Alert from "shared/ui/Alert";
import BuyCreditsCard from "components/BuyCreditsCard";
import DeleteAccountModal from "features/account/components/DeleteAccountModal";
import { ReactComponent as IconDelete } from "assets/img/icons/icon_Trash.svg";
import { ReactComponent as IconUsers } from "assets/img/icons/icon_Users.svg";
import { ReactComponent as IconVerified } from "assets/img/icons/icon_status-Verified.svg";
import { ReactComponent as IconWarning } from "assets/img/icons/icon_status-Warning.svg";
import PageHeader from "components/PageHeader";
import PriceCard from "components/PriceCard/PriceCard";
import ProfilCard from "components/ProfilCard/ProfilCard";
import ResetPassword from "components/ResetPassword";
import UploadProfilePicture from "features/account/components/UploadProfilePicture";
import { UserContext } from "features/account";
import { getSubscription } from "features/account";
import { getSubscriptionStatus, cancelSubscription, reactivateSubscription } from "features/payment";
import logoIdTracker from "assets/img/ID Tracker.svg";
import logoProfil from "assets/img/Accompagnement.svg";
import logosecurity from "assets/img/security.svg";
import {useLocation, useNavigate} from "react-router-dom";
import {mailTo} from "shared/lib/email_utils";
import {Tooltip} from "react-tooltip";
import Modal from "shared/ui/Modal";
import SponsorShipModal from "components/SponsorShipModal";
import Accordion from "shared/ui/Accordion";
import { mfaService } from "features/mfa";
import { MFASetupModal } from "features/mfa";
import { MFADisableModal } from "features/mfa";
import { SessionsList } from "features/mfa";

const Profil: FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, userType, isVerified, getMe } = useContext(UserContext);
    const [mfaStatus, setMfaStatus] = useState<{ isEnabled: boolean; type: string; hasBackupCodes: boolean } | null>(null);
    const [showMFASetupModal, setShowMFASetupModal] = useState(false);
    const [showMFADisableModal, setShowMFADisableModal] = useState(false);
    const [showSessions, setShowSessions] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getMe()
    }, [location.pathname, user?.email]);

    useEffect(() => {
        // Load MFA status when user is loaded
        if (user?.id) {
            loadMFAStatus();
        }
    }, [user?.id]);

    const loadMFAStatus = async () => {
        try {
            setLoading(true);
            const status = await mfaService.getMFAStatus(userType ?? 'individual');
            setMfaStatus(status);
        } catch (error) {
            console.error('Error loading MFA status:', error);
        } finally {
            setLoading(false);
        }
    };

    const [offer, setOffer] = useState<any>();
    const type = user?.type;
    const [showSponsorModal, setShowSponsorModal] = useState<boolean>(false);
    const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);

    const handleVerifyIdentity = () => {
        navigate(`${userType === 'partner' ? '/professionnels' : '/particuliers'}/idtracker`);
    };

    // État de l'abonnement lu en direct chez Stripe : terme de la période et
    // résiliation éventuellement demandée. On n'en garde pas de copie en base,
    // qu'il faudrait ensuite resynchroniser.
    const [abonnement, setAbonnement] = useState<any>(null);
    const [actionAbonnement, setActionAbonnement] = useState(false);
    const [messageAbonnement, setMessageAbonnement] = useState<string | null>(null);
    const [showResiliationModal, setShowResiliationModal] = useState(false);

    const chargerAbonnement = async () => {
        const etat = await getSubscriptionStatus();
        setAbonnement(etat?.hasSubscription ? etat : null);
    };

    useEffect(() => {
        const getOffer = async () => {
            try {
                const response = await getSubscription(type, user?.email);
                setOffer(response.data.offrePurchase);
            } catch (error) {
                console.log(error);
            }
        };

        if (user) {
            getOffer();
            chargerAbonnement();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, type]);

    const formaterDate = (iso?: string) =>
        iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "";

    // « ID Tracker : ID Protect Premium Hub » débordait de la carte. Le préfixe
    // n'est retiré QUE de l'affichage : le libellé complet reste celui envoyé au
    // serveur pour résoudre le tarif Stripe.
    const nomOffreCourt = (nom?: string) => (nom ?? "").replace(/^ID Protect\s+/i, "");

    const handleResilier = async () => {
        setShowResiliationModal(false);
        setActionAbonnement(true);
        const res = await cancelSubscription();
        setActionAbonnement(false);
        if (res?.success) {
            setMessageAbonnement(`Résiliation enregistrée. Votre accès reste ouvert jusqu'au ${formaterDate(res.finDePeriode)}.`);
            chargerAbonnement();
        } else {
            setMessageAbonnement(res?.error ?? "La résiliation n'a pas pu être enregistrée. Merci de réessayer.");
        }
    };

    const handleReactiver = async () => {
        setActionAbonnement(true);
        const res = await reactivateSubscription();
        setActionAbonnement(false);
        if (res?.success) {
            setMessageAbonnement("Votre abonnement est de nouveau reconduit automatiquement.");
            chargerAbonnement();
        } else {
            setMessageAbonnement(res?.error ?? "La réactivation n'a pas pu être enregistrée. Merci de réessayer.");
        }
    };

    const handleEnableMFA = () => {
        setShowMFASetupModal(true);
    };

    const handleDisableMFA = () => {
        setShowMFADisableModal(true);
    };

    const handleMFASuccess = () => {
        loadMFAStatus(); // Reload MFA status after successful setup/disable
    };

    const handleViewSessions = () => {
        setShowSessions(true);
    };

    return (
      <div data-tooltip-id="tooltip-change-password"
           data-tooltip-place="right"
           data-tooltip-content="Veuillez changer votre mot de passe"
           className="profil">
          {user?.mustChangeThePassword && !+(process.env.REACT_APP_FOR_ADV || 0) && (
            <Tooltip id="tooltip-change-password" variant="error" float={true} className="text-lowercase"/>
          )}
          <PageHeader
            title={"Profil"}
            description={"Mon profil Id Protect"}
            notification={user?.type === 'individual'}
            protectAlert={user?.type === 'individual'}
          >
          </PageHeader>
          <div className="content__body">
              <div className="row mb-1">
                  <div className="col-sm-7">
                      <ProfilCard
                        title="profil"
                        logo={logoProfil}>
                          <div className="d-flex gap-2 flex-wrap">
                              <UploadProfilePicture />
                              <div>
                                  {user && (
                                    <h3 className="mb-1 ProfilCard__title">
                                        {user?.firstname + " " + user?.lastname + " "}
                                        {(isVerified === true || user?.type === 'partner') && (
                                          <div>
                                              <div className={`isVerified show tag-icon tag-success ml-2`}>
                                                  <IconVerified
                                                    fill="#17EB79"
                                                    height={16}
                                                    width={16}
                                                  />
                                                  {" "}Compte vérifié
                                              </div>
                                          </div>
                                        )}
                                        {isVerified === false && user?.type !== 'partner' && (
                                          <>
                                              <div className="d-flex align-items-center gap-1">
                                                        <span data-tooltip-id="my-tooltip"
                                                              data-tooltip-place="top"
                                                              data-tooltip-content="veuillez ajouter un document en suivi"
                                                              className={`isVerified show tag-icon tag-error ml-2`}>
                                                            Compte non vérifié
                                                        </span>
                                                  <Tooltip id="my-tooltip" variant="info" float={true} className="text-lowercase font-SM"/>
                                                  <Button
                                                    type={Button_Type.PRIMARY}
                                                    style={Button_Style.SOLID}
                                                    label="Vérifier"
                                                    onClick={handleVerifyIdentity}
                                                    className="ml-2"
                                                  />
                                              </div>
                                          </>
                                        )}
                                        {isVerified == null && (
                                          <span className={`isVerified tag tag-icon ml-2`}>
                                                </span>
                                        )}
                                    </h3>
                                  )}
                                  {user?.email && <p className="mb-1">{user?.email}</p>}
                                  {user?.phoneNumber && <p className="mb-1">{user?.phoneNumber}</p>}
                              </div>
                              <div>
                                  {!+(process.env.REACT_APP_FOR_PARTNER_ONLY || 0) && (
                                    <div>
                                        <Button
                                          size={"50px"}
                                          icon={<IconUsers />}
                                          type={Button_Type.PRIMARY}
                                          style={Button_Style.SOLID}
                                          label="Parainer un proche"
                                          onClick={()=> {setShowSponsorModal(true)}}
                                          className="ml-2"
                                        />
                                        <Button
                                          type={Button_Type.Error}
                                          style={Button_Style.OUTLINED}
                                          label="Supprimer mon compte"
                                          icon={<IconDelete />}
                                          onClick={() => setIsModalDeleteOpen(!isModalDeleteOpen)}
                                        />
                                    </div>
                                  )}
                              </div>
                          </div>
                      </ProfilCard>
                  </div>
                  {!+(process.env.REACT_APP_FOR_ADV || 0) && (
                    <div className="col-sm-5">
                        <ProfilCard
                          title="Sécurité"
                          logo={logosecurity}
                        >
                            <Accordion title="Réinitialiser son mot de passe">
                                <ResetPassword
                                  isForgotPassword={user?.type === "partner" ? false : user?.mustChangeThePassword} />
                            </Accordion>

                            {!+(process.env.REACT_APP_FOR_PARTNER_ONLY || 0) && (
                              <Accordion title="Authentification à deux facteurs" expand={true}>
                                  {!loading && mfaStatus && (
                                    <div className="mfa-status">
                                        {!mfaStatus.isEnabled ? (
                                          // MFA is disabled
                                          <div>
                                              <div className="mfa-info">
                                                        <span className={"text-primary text-sm bold"}>
                                                            Cela n'est pas encore activée.
                                                        </span>
                                                  <div className="mt-2">
                                                            <span className={"text-muted-foreground text-xs text-justify"}>
                                                                L'authentification à deux facteurs ajoute une couche de sécurité supplémentaire
                                                                à votre compte en exigeant plus qu'un simple mot de passe pour se connecter.
                                                            </span>
                                                  </div>
                                              </div>
                                              <Button
                                                type={Button_Type.PRIMARY}
                                                style={Button_Style.SOLID}
                                                label="Activer"
                                                onClick={handleEnableMFA}
                                                className="mt-2"
                                              />
                                          </div>
                                        ) : (
                                          // MFA is enabled
                                          <div>
                                              <div className="mfa-enabled-info">

                                                  {mfaStatus.hasBackupCodes && (
                                                    <div className="backup-codes-info mt-2">
                                                                <span className="text-muted-foreground text-xs">
                                                                    Des codes de secours sont disponibles
                                                                </span>
                                                    </div>
                                                  )}

                                                  <div className="mfa-actions mt-3">
                                                      <Button
                                                        type={Button_Type.PRIMARY}
                                                        style={Button_Style.OUTLINED}
                                                        label="Gérer les sessions actives"
                                                        onClick={handleViewSessions}
                                                        className="mr-2"
                                                      />
                                                      <Button
                                                        type={Button_Type.Error}
                                                        style={Button_Style.OUTLINED}
                                                        label="Désactiver"
                                                        onClick={handleDisableMFA}
                                                      />
                                                  </div>
                                              </div>
                                          </div>
                                        )}
                                    </div>
                                  )}

                                  {loading && (
                                    <div className="text-center p-3">
                                        <span>Chargement...</span>
                                    </div>
                                  )}
                              </Accordion>
                              )}
                        </ProfilCard>
                    </div>
                  )}
              </div>
              {!+(process.env.REACT_APP_FOR_PARTNER_ONLY || 0) && (<ProfilCard
                title="Votre abonnement"
                logo={logoIdTracker}>
                  <div className="row mb-1">
                      <div className="col-sm-7">
                          <PriceCard
                            monthly={!offer?.isYear}
                            title={`Id Tracker ${offer?.name ? ": " + nomOffreCourt(offer?.name) : ": Gratuite"}`}
                            logo={logoIdTracker}
                            price={`${offer?.price ? offer?.price : "0"}€`}
                            subtitle="Restez informé quand votre document est présenté">
                              <p>
                                  Prévenez l'utilisation frauduleuse de vos documents d'identité en vous opposant à leur utilisation en
                                  temps réel.
                              </p>
                              <div className="d-flex gap-1 ProfilCard__buttons">
                                  <a
                                    className={`btn ${userType === "partner" ? " btn-secondary" : "btn-primary"} btn-solid`}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => navigate(`${userType === 'partner' ? '/professionnels' : '/particuliers'}/id-tracker-pricing`)}
                                  >
                                      Changer d'abonnement
                                  </a>
                                  {/* Résiliation en libre-service. L'ancien bouton ouvrait le
                                      logiciel de messagerie du client : quelqu'un devait ensuite
                                      résilier à la main dans Stripe, et tant que personne ne le
                                      faisait, les prélèvements continuaient. */}
                                  {abonnement && !abonnement.resiliationDemandee && (
                                    <Button
                                      style={Button_Style.OUTLINED}
                                      type={userType === "partner" ? Button_Type.SECONDARY : Button_Type.PRIMARY}
                                      label={actionAbonnement ? "..." : "Résilier"}
                                      disabled={actionAbonnement}
                                      onClick={() => setShowResiliationModal(true)}
                                    />
                                  )}
                                  {abonnement && abonnement.resiliationDemandee && (
                                    <Button
                                      style={Button_Style.OUTLINED}
                                      type={userType === "partner" ? Button_Type.SECONDARY : Button_Type.PRIMARY}
                                      label={actionAbonnement ? "..." : "Reprendre l'abonnement"}
                                      disabled={actionAbonnement}
                                      onClick={handleReactiver}
                                    />
                                  )}
                              </div>

                              {/* L'échéance est une information d'état, pas un
                                  paragraphe : elle se lit d'un coup d'œil. */}
                              {abonnement?.resiliationDemandee && (
                                <p className="PriceCard__etat PriceCard__etat--resilie">
                                    Résilié — accès ouvert jusqu'au {formaterDate(abonnement.finDePeriode)}, sans reconduction.
                                </p>
                              )}
                              {abonnement && !abonnement.resiliationDemandee && (
                                <p className="PriceCard__etat">
                                    Prochaine échéance le {formaterDate(abonnement.finDePeriode)}.
                                </p>
                              )}
                              {messageAbonnement && <p className="PriceCard__etat">{messageAbonnement}</p>}
                          </PriceCard>
                      </div>
                      <div className="col-sm-5">
                          <BuyCreditsCard showSponsorModal={showSponsorModal}
                                          setShowSponsorModal={setShowSponsorModal} />
                      </div>
                  </div>
              </ProfilCard>)}
          </div>

          {/* MFA Setup Modal */}
          {/* Résiliation : une décision qui engage, donc une confirmation qui
              dit exactement ce qui va se passer — et surtout ce qui NE se passe
              pas. La boîte de dialogue du navigateur ne permettait ni de le
              formuler, ni de rappeler que le geste reste réversible. */}
          {showResiliationModal && (
            <Modal
              isOpen={showResiliationModal}
              onClose={() => setShowResiliationModal(false)}
              onConfirm={handleResilier}
              labelOnConfirm="Confirmer la résiliation"
              title="Résilier votre abonnement"
              subtitle={offer?.name ? nomOffreCourt(offer.name) : undefined}
              type="warning"
              bgBody="bg-light">
                <div className="Profil__resiliation">
                    <p>
                        Votre accès reste ouvert jusqu'au <strong>{formaterDate(abonnement?.finDePeriode)}</strong>.
                        À cette date, l'abonnement ne sera pas reconduit et aucun nouveau prélèvement n'aura lieu.
                    </p>
                    <ul>
                        <li>Vous conservez toutes les fonctionnalités jusqu'à cette date.</li>
                        <li>L'année déjà réglée ne fait l'objet d'aucun remboursement.</li>
                        <li>Vous pouvez reprendre l'abonnement à tout moment avant l'échéance.</li>
                    </ul>
                    <p className="Profil__resiliationNote">
                        Vos vérifications déjà achetées et vos accompagnements restent acquis : ils ne dépendent pas de l'abonnement.
                    </p>
                </div>
            </Modal>
          )}

          {showMFASetupModal && (
            <MFASetupModal
              isOpen={showMFASetupModal}
              onClose={() => setShowMFASetupModal(false)}
              onSuccess={handleMFASuccess}
              userEmail={user?.email || ''}
            />
          )}

         {/*MFA Disable Modal*/}
          {showMFADisableModal && (
            <MFADisableModal
              isOpen={showMFADisableModal}
              onClose={() => setShowMFADisableModal(false)}
              onSuccess={handleMFASuccess}
              mfaType={mfaStatus?.type || 'email'}
            />
          )}

           {/*Sessions List Modal*/}
          {showSessions && (
            <Modal
              isOpen={showSessions}
              onClose={() => setShowSessions(false)}
              title="Sessions actives"
              infoModal={true}
            >
                <SessionsList onClose={() => setShowSessions(false)} />
            </Modal>
          )}

          {showSponsorModal && (
            <SponsorShipModal isOpen={showSponsorModal}
                              onClose={() => setShowSponsorModal(false)}
            />
          )}

          {type && (
            <DeleteAccountModal
              isOpen={isModalDeleteOpen}
              onClose={() => setIsModalDeleteOpen(false)}
            />
          )}
      </div>
    );
};

export default Profil;